import React, { useEffect, useMemo, useState } from "react";
import {
  calculateCountdownParts,
  formatScheduledStart,
  getScheduleStartDate,
} from "../../utils/stageSchedule";

const formatUnit = (value) => (value > 99 ? String(value) : String(value).padStart(2, "0"));

function CountdownUnit({ label, value }) {
  return (
    <div className="min-w-0 rounded-2xl border border-slate-200 bg-white px-4 py-5 shadow-[0_22px_55px_-42px_rgba(15,23,42,0.65)] sm:px-6 sm:py-7">
      <p className="font-mono text-4xl font-semibold leading-none tracking-[-0.08em] text-slate-950 sm:text-6xl">
        {formatUnit(value)}
      </p>
      <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </p>
    </div>
  );
}

export default function ScheduleCountdown({ stage, onComplete, completeContent = null }) {
  const [now, setNow] = useState(() => new Date());
  const schedule = stage?.schedule;
  const startDate = useMemo(() => getScheduleStartDate(schedule), [schedule]);
  const countdown = useMemo(() => calculateCountdownParts(startDate, now), [startDate, now]);
  const hasCompleted = Boolean(countdown?.isComplete);
  const visibleCountdown = countdown || { days: 0, hours: 0, minutes: 0 };
  const accentColor = stage?.colors?.header || "#111827";
  const scheduledLabel = startDate
    ? formatScheduledStart(startDate, schedule?.timezone)
    : "Start time not set";

  useEffect(() => {
    if (!startDate) return undefined;

    const interval = window.setInterval(() => {
      const nextNow = new Date();
      setNow(nextNow);
      if (startDate.getTime() <= nextNow.getTime()) onComplete?.();
    }, 1000);

    return () => window.clearInterval(interval);
  }, [startDate, onComplete]);

  useEffect(() => {
    if (countdown?.isComplete && !completeContent) onComplete?.();
  }, [countdown, completeContent, onComplete]);

  if (hasCompleted && !completeContent) return null;

  return (
    <section className="w-full max-w-4xl px-0 text-slate-950 sm:px-4" aria-live="polite">
      <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-slate-50/95 p-5 shadow-[0_34px_110px_-70px_rgba(2,6,23,0.85)] backdrop-blur sm:p-8 lg:p-10">
        <div
          className="absolute left-0 top-0 h-1 w-full"
          style={{ backgroundColor: accentColor }}
          aria-hidden="true"
        />

        <div className="grid gap-8 lg:grid-cols-[minmax(0,0.82fr)_minmax(260px,0.44fr)] lg:items-end">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
              <span
                className={hasCompleted ? "h-2 w-2 rounded-full" : "h-2 w-2 rounded-full animate-pulse"}
                style={{ backgroundColor: hasCompleted ? "#16a34a" : accentColor }}
                aria-hidden="true"
              />
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                {hasCompleted ? "Ready to watch" : "Scheduled broadcast"}
              </span>
            </div>

            <h2 className="mt-6 max-w-2xl text-3xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
              {hasCompleted
                ? "The scheduled start time has arrived"
                : stage?.title || stage?.event_name || "This video starts soon"}
            </h2>
            <p className="mt-4 max-w-[62ch] text-sm leading-6 text-slate-600 sm:text-base">
              {hasCompleted
                ? "The countdown has ended. Complete the access step below to watch the video."
                : "The video will unlock automatically when the scheduled start time arrives."}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Starts
            </p>
            <p className="mt-2 text-sm font-semibold leading-5 text-slate-950">
              {scheduledLabel}
            </p>
            <p className="mt-3 font-mono text-xs text-slate-500">
              {schedule?.timezone || "UTC"}
            </p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-4">
          <CountdownUnit label="Days" value={visibleCountdown.days} />
          <CountdownUnit label="Hours" value={visibleCountdown.hours} />
          <CountdownUnit label="Minutes" value={visibleCountdown.minutes} />
        </div>

        {hasCompleted && completeContent && (
          <div className="mt-6 border-t border-slate-200 pt-6">
            {completeContent}
          </div>
        )}
      </div>
    </section>
  );
}
